import { useNavigate, useParams } from "react-router-dom";
import { edit } from "../../asset";
import SubHeadings from "../../components/SubHeadings";
import { SalaryStructureResponse } from "../../types/payrollType";

interface Props {
  salary: SalaryStructureResponse;
}

export default function SalaryOverView({ salary }: Props) {
  const navigate = useNavigate();
  const { id } = useParams();

  const salaryComponents = (() => {
    if (!salary) return [];

    const mainComponents = [
      {
        name: "Basic Salary",
        monthly: salary.basicSalary ?? 0,
        annual: (salary.basicSalary ?? 0) * 12,
      },
      {
        name: "PF Amount",
        monthly: salary.pfAmount ?? 0,
        annual: (salary.pfAmount ?? 0) * 12,
      },
      {
        name: "Professional Tax",
        monthly: salary.professionalTaxAmount ?? 0,
        annual: (salary.professionalTaxAmount ?? 0) * 12,
      },
    ];

    const otherComponents = Object.entries(
      salary.otherSalaryComponents || {},
    ).map(([key, value]) => ({
      name: key,
      monthly: Number(value) ?? 0,
      annual: Number(value ?? 0) * 12,
    }));

    return [...mainComponents, ...otherComponents];
  })();

  const totalMonthly = salaryComponents.reduce(
    (sum, item) => sum + item.monthly,
    0,
  );

  const totalAnnual = salaryComponents.reduce(
    (sum, item) => sum + item.annual,
    0,
  );

  const handleSalaryDetailsEdit = () => {
    const staffId = id;
    const storageKey = `payroll-progress-${staffId}`;

    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        step: 2,
        completed: false,
      }),
    );

    navigate(`/payroll-management/add-payroll/${staffId}?step=2`);
  };

  return (
    <div className="bg-white py-[18px] px-[22px] max-w-2xl w-full rounded-lg flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center">
        <SubHeadings
          text="Salary Structure"
          textColor="text-neutral-400"
          bgColor="bg-neutral-400"
        />

        <button
          onClick={handleSalaryDetailsEdit}
          className="bg-warning-50 cursor-pointer py-1 px-2 flex gap-2 items-center rounded"
        >
          <img src={edit} alt="" />
          <span className="text-warning-700 text-xs font-medium">Edit</span>
        </button>
      </div>

      <div className="grid grid-cols-[220px_160px_160px] text-nowrap gap-4 mt-8 px-2">
        <span className="text-[13px] font-semibold text-neutral-300">
          Salary Component
        </span>
        <span className="text-[13px] font-semibold text-neutral-300">
          Monthly Amount
        </span>
        <span className="text-[13px] font-semibold text-neutral-300">
          Annual Amount
        </span>
      </div>

      <div className="flex flex-col gap-4 px-2 mt-4">
        {salaryComponents.length === 0 ? (
          <p className="text-sm text-neutral-400">No salary data available</p>
        ) : (
          salaryComponents.map((item, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_170px_160px] items-start"
            >
              <p className="text-sm text-neutral-500 font-medium">
                {item.name}
              </p>
              <p className="text-sm text-neutral-500 font-medium">
                ₹{item.monthly}
              </p>
              <p className="text-sm text-neutral-500 font-medium">
                ₹{item.annual}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Total */}
      <div className="bg-success-50 py-4 mt-6 rounded-lg px-2 grid grid-cols-[1fr_170px_160px] items-center">
        <span className="text-success-800 text-base font-medium">
          Total Earnings
        </span>
        <span className="text-neutral-500 font-semibold text-lg">
          ₹{totalMonthly}
        </span>
        <span className="text-neutral-500 font-semibold text-lg">
          ₹{totalAnnual}
        </span>
      </div>
    </div>
  );
}
